import { useQuery } from '@tanstack/react-query';
import { parseHttpError } from '@upme/api-client';
import { type AdminAdjustCoinsRequest, type AdminUserDto, AdminUserSearchQuery } from '@upme/contracts';
import { Button } from '@upme/ui/components/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@upme/ui/components/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@upme/ui/components/dialog';
import { Input } from '@upme/ui/components/input';
import { Label } from '@upme/ui/components/label';
import { Popover, PopoverContent, PopoverTrigger } from '@upme/ui/components/popover';
import { cn } from '@upme/ui/lib/utils';
import { Check, ChevronsUpDown, Coins } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { formatEnergyDelta } from '../../../shared/format-energy';
import { searchByUsernameOptions } from '../../users/queries';
import { useAdjustCoins } from '../mutations';

type Mode = 'delta' | 'setTo';

export interface SelectedUser {
  id: string;
  username: string | null;
  firstName: string | null;
}

export function userLabel(u: { username: string | null; firstName: string | null }): string {
  const name = u.firstName?.trim();
  const handle = u.username ? `@${u.username}` : null;
  return [name, handle].filter(Boolean).join(' ') || 'без имени';
}

/**
 * Чистый сборщик тела запроса. Вынесен ради юнит-теста: вся развилка delta|setTo
 * (и опциональный note) тестируется без рендера и моков сети.
 */
export function buildAdjustBody(args: {
  userId: string;
  mode: Mode;
  amount: number;
  note?: string;
}): AdminAdjustCoinsRequest {
  const note = args.note?.trim();
  const base = note ? { userId: args.userId, note } : { userId: args.userId };
  return args.mode === 'delta' ? { ...base, delta: args.amount } : { ...base, setTo: args.amount };
}

export function AdjustCoinsDialog() {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<SelectedUser | null>(null);
  const [mode, setMode] = useState<Mode>('delta');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const adjust = useAdjustCoins();

  function reset() {
    setSelected(null);
    setMode('delta');
    setAmount('');
    setNote('');
  }

  function onOpenChange(next: boolean) {
    setOpen(next);
    if (!next) reset();
  }

  async function submit() {
    if (!selected) {
      toast.error('Выберите пользователя');
      return;
    }
    const value = Number(amount);
    if (amount.trim() === '' || !Number.isInteger(value)) {
      toast.error('Сумма должна быть целым числом');
      return;
    }
    if (mode === 'delta' && value === 0) {
      toast.error('Дельта не может быть 0');
      return;
    }
    if (mode === 'setTo' && value < 0) {
      toast.error('Баланс не может быть отрицательным');
      return;
    }

    try {
      const res = await adjust.mutateAsync(
        buildAdjustBody({ userId: selected.id, mode, amount: value, note }),
      );
      toast.success(`Начислено ${formatEnergyDelta(res.delta)} энергии — ${userLabel(selected)}`);
      onOpenChange(false);
    } catch (err) {
      const api = await parseHttpError(err);
      toast.error(api?.message ?? 'Не удалось начислить');
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button>
          <Coins className="mr-2 h-4 w-4" />
          Начислить энергию
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Ручная корректировка баланса</DialogTitle>
          <DialogDescription>
            Создаёт транзакцию MANUAL_ADJUST. Баланс применится воркером за пару секунд.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Пользователь</Label>
            <UserPicker value={selected} onChange={setSelected} />
          </div>

          <div className="space-y-1.5">
            <Label>Операция</Label>
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant={mode === 'delta' ? 'default' : 'outline'}
                onClick={() => setMode('delta')}
              >
                Прибавить (±)
              </Button>
              <Button
                type="button"
                variant={mode === 'setTo' ? 'default' : 'outline'}
                onClick={() => setMode('setTo')}
              >
                Выставить (=)
              </Button>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="adjust-amount">
              {mode === 'delta' ? 'На сколько (можно −)' : 'Новый баланс'}
            </Label>
            <Input
              id="adjust-amount"
              type="number"
              step={1}
              inputMode="numeric"
              placeholder={mode === 'delta' ? 'например, 100 или −50' : 'например, 250'}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="adjust-note">Заметка (опц.)</Label>
            <Input
              id="adjust-note"
              placeholder="зачем начисляем"
              value={note}
              maxLength={280}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button onClick={submit} disabled={adjust.isPending}>
            {adjust.isPending ? 'Начисляем…' : 'Начислить'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UserPicker({
  value,
  onChange,
}: {
  value: SelectedUser | null;
  onChange: (u: SelectedUser | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Бэкенд ищет по точному telegram-username (буквы/цифры/подчёркивание); чтобы
  // не слать заведомо 400-ные запросы, дёргаем поиск только на валидном вводе.
  const valid = AdminUserSearchQuery.safeParse({ username: search }).success;
  const q = useQuery({ ...searchByUsernameOptions(search), enabled: open && valid });
  const results: AdminUserDto[] = q.data ?? [];

  function pick(u: AdminUserDto) {
    onChange({ id: u.id, username: u.username, firstName: u.firstName });
    setOpen(false);
    setSearch('');
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" className="w-full justify-between">
          <span className="truncate text-left">{value ? userLabel(value) : 'Найти по username…'}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0">
        <Command shouldFilter={false}>
          <CommandInput placeholder="username (без @)" value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty>
              {!valid && search.length > 0
                ? 'Только буквы, цифры и _'
                : q.isFetching
                  ? 'Ищем…'
                  : 'Не найдено'}
            </CommandEmpty>
            <CommandGroup>
              {results.map((u) => (
                <CommandItem key={u.id} value={u.id} onSelect={() => pick(u)}>
                  <Check className={cn('mr-2 h-4 w-4', value?.id === u.id ? 'opacity-100' : 'opacity-0')} />
                  <span className="truncate">{userLabel(u)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
